import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  formatDateInput,
  parseDateInput,
  getDateFormatPlaceholder,
  formatMonthYear,
  academyTodayDate,
} from "../../utils/regionalFormat";

export function DatePicker({ value, onChange, className = "", clearable = false, disabled = false, placeholder, maxDate = null }) {
  const { t } = useTranslation();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const popupRef = useRef(null);

  const parsedValue = parseDateInput(value) || null;

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(parsedValue);
  const [viewDate, setViewDate] = useState(parsedValue || academyTodayDate());
  const [popupStyle, setPopupStyle] = useState({ top: 0, left: 0, minWidth: 240 });

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const display = value || (selected ? formatDateInput(selected) : "");
  const datePlaceholder = placeholder || getDateFormatPlaceholder();

  const isAfterMaxDate = (d) => {
    if (!maxDate || !d) return false;
    const cap = new Date(maxDate);
    cap.setHours(23, 59, 59, 999);
    const candidate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    return candidate.getTime() > cap.getTime();
  };

  useEffect(() => {
    const parsed = parseDateInput(value);
    if (parsed) {
      setSelected(parsed);
      setViewDate(parsed);
    } else {
      setSelected(null);
    }
  }, [value]);

  const updatePopupPosition = () => {
    const input = inputRef.current;
    if (!input) return;

    const rect = input.getBoundingClientRect();
    const popupHeight = popupRef.current?.offsetHeight || 280;
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < popupHeight + gap && rect.top > popupHeight + gap;

    setPopupStyle({
      top: openAbove ? rect.top - popupHeight - gap : rect.bottom + gap,
      left: rect.left,
      minWidth: Math.max(rect.width, 240),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePopupPosition();
  }, [open, viewDate]);

  useEffect(() => {
    if (!open) return;

    const handleReposition = () => updatePopupPosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [open, viewDate]);

  const handleClear = (e) => {
    e.stopPropagation();
    setSelected(null);
    setOpen(false);
    onChange?.("");
  };

  useEffect(() => {
    const handler = (e) => {
      const target = e.target;
      if (
        wrapperRef.current?.contains(target)
        || popupRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const changeMonth = (delta) => {
    const d = new Date(viewDate);
    d.setMonth(d.getMonth() + delta);
    setViewDate(d);
  };

  const changeYear = (y) => {
    const d = new Date(viewDate);
    d.setFullYear(y);
    setViewDate(d);
  };

  const selectDate = (d) => {
    if (isAfterMaxDate(d)) return;
    setSelected(d);
    setViewDate(d);
    setOpen(false);
    onChange?.(formatDateInput(d));
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    const d = parseDateInput(v);
    if (d) {
      if (isAfterMaxDate(d)) return;
      setSelected(d);
      setViewDate(d);
      onChange?.(v);
    } else {
      onChange?.(v);
    }
  };

  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  const days = [];

  for (let i = 0; i < start.getDay(); i++) days.push(null);
  for (let d = 1; d <= end.getDate(); d++) days.push(new Date(year, month, d));

  const years = [];
  const currentYear = academyTodayDate().getFullYear();
  const maxYear = maxDate ? maxDate.getFullYear() : currentYear + 10;
  for (let y = currentYear - 100; y <= maxYear; y++) {
    years.push(y);
  }

  const calendarPopup = open ? (
    <div
      ref={popupRef}
      className="fixed z-[200] bg-white border border-gray-200 rounded-lg shadow-xl p-2"
      style={{
        top: popupStyle.top,
        left: popupStyle.left,
        minWidth: popupStyle.minWidth,
      }}
    >
      <div className="flex items-center justify-between mb-2 gap-2">
        <button type="button" onClick={() => changeMonth(-1)} className="px-2 py-1 rounded hover:bg-gray-100">
          &lt;
        </button>

        <div className="flex gap-2 items-center">
          <span>{formatMonthYear(viewDate)}</span>

          <select
            value={year}
            onChange={(e) => changeYear(Number(e.target.value))}
            className="border rounded px-1"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <button type="button" onClick={() => changeMonth(1)} className="px-2 py-1 rounded hover:bg-gray-100">
          &gt;
        </button>
      </div>

      <table className="w-full text-center text-sm">
        <thead>
          <tr>
            {/* Calendar grid is Sunday-first; days.short keys are ISO (1=Mon … 7=Sun). */}
            {[7, 1, 2, 3, 4, 5, 6].map((dayKey) => (
              <th key={dayKey} className="font-medium text-gray-500 pb-1">
                {t(`days.short.${dayKey}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: Math.ceil(days.length / 7) }).map((_, r) => (
            <tr key={r}>
              {days.slice(r * 7, r * 7 + 7).map((d, i) => (
                <td key={i} className="p-1">
                  {d ? (
                    <button
                      type="button"
                      disabled={isAfterMaxDate(d)}
                      onClick={() => selectDate(d)}
                      className={`w-8 h-8 rounded ${isAfterMaxDate(d)
                        ? "text-gray-300 cursor-not-allowed"
                        : selected && d.toDateString() === selected.toDateString()
                        ? "bg-primary-600 text-white"
                        : "hover:bg-gray-200"
                        }`}
                    >
                      {d.getDate()}
                    </button>
                  ) : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : null;

  return (
    <div className={`relative block ${className}`} ref={wrapperRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={display}
          onChange={handleInputChange}
          onFocus={() => !disabled && setOpen(true)}
          placeholder={datePlaceholder}
          disabled={disabled}
          className={`input ${clearable && display ? "pr-8" : ""} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        />
        {clearable && display && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 focus:outline-none text-lg leading-none"
            title={t('datePicker.clearDate')}
          >
            ×
          </button>
        )}
      </div>

      {typeof document !== "undefined" && createPortal(calendarPopup, document.body)}
    </div>
  );
}

export default DatePicker;
